import type { ClipboardPayload } from 'helpers/tableClipboard.helper/tableClipboard.helper.types'
import type { TimeSeriesDataTableData } from './types'

export type TimeSeriesTableClipboardCellPosition = {
  rowIndex: number
  columnIndex: number
}

export type TimeSeriesTableClipboardSelectionInput = {
  selectedCells: Set<string>
  rows: TimeSeriesDataTableData[]
  selectableColumnIds: string[]
}

export interface TimeSeriesTableClipboardSelectionSourceAdapter {
  toClipboardPayload: (input: TimeSeriesTableClipboardSelectionInput) => ClipboardPayload
}

export function getTimeSeriesClipboardCellId(rowIndex: number, columnIndex: number): string {
  return `${rowIndex}-${columnIndex}`
}

export function timeSeriesClipboardCellIdToPosition(cellId: string): TimeSeriesTableClipboardCellPosition {
  const [rowIndex, columnIndex] = cellId.split('-').map(Number)
  return { rowIndex, columnIndex }
}

export function getTimeSeriesRectangularClipboardSelection(
  anchor: TimeSeriesTableClipboardCellPosition,
  current: TimeSeriesTableClipboardCellPosition
): Set<string> {
  const startRow = Math.min(anchor.rowIndex, current.rowIndex)
  const endRow = Math.max(anchor.rowIndex, current.rowIndex)
  const startCol = Math.min(anchor.columnIndex, current.columnIndex)
  const endCol = Math.max(anchor.columnIndex, current.columnIndex)

  const selection = new Set<string>()

  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
    for (let columnIndex = startCol; columnIndex <= endCol; columnIndex++) {
      selection.add(getTimeSeriesClipboardCellId(rowIndex, columnIndex))
    }
  }

  return selection
}

export const timeSeriesTableClipboardSelectionSourceAdapter: TimeSeriesTableClipboardSelectionSourceAdapter = {
  toClipboardPayload({
    selectedCells,
    rows,
    selectableColumnIds,
  }: TimeSeriesTableClipboardSelectionInput): ClipboardPayload {
    if (selectedCells.size === 0) {
      return []
    }

    const selectedPositions = Array.from(selectedCells).map(timeSeriesClipboardCellIdToPosition)
    const minRow = Math.min(...selectedPositions.map((position) => position.rowIndex))
    const maxRow = Math.max(...selectedPositions.map((position) => position.rowIndex))
    const minCol = Math.min(...selectedPositions.map((position) => position.columnIndex))
    const maxCol = Math.max(...selectedPositions.map((position) => position.columnIndex))

    const payload: ClipboardPayload = []

    for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex++) {
      const payloadRow: string[] = []

      for (let columnIndex = minCol; columnIndex <= maxCol; columnIndex++) {
        const isSelected = selectedCells.has(getTimeSeriesClipboardCellId(rowIndex, columnIndex))

        if (!isSelected) {
          payloadRow.push('')
          continue
        }

        const columnId = selectableColumnIds[columnIndex]
        const value = rows[rowIndex]?.[columnId]
        payloadRow.push(value == null ? '' : String(value))
      }

      payload.push(payloadRow)
    }

    return payload
  },
}
